import { useState, useMemo, useContext, useCallback } from 'react';
import { Alert, Button, Icon, Select, TextArea } from '@aidkitorg/component-library';
import { useLocalizedStrings } from '../Localization';
import { AKMeta } from '@aidkitorg/types/lib/convoai/conversation';
import { ChatConversationContext } from '../Contexts/ChatContext';
import InterfaceContext from '../Context';
import { usePost } from '../API';
import { ChatSessionContext } from '../Contexts/ChatSessionContext';

export const FeedbackButtons = (props: AKMeta["feedback"] & { id: string, createdAt?: number }) => {
  const { dispatch } = useContext(ChatConversationContext);
  const updateFeedback = usePost('/coach/conversation/feedback');
  const { lang } = useContext(InterfaceContext);
  const L = useLocalizedStrings();
  const [additionalFeedbackInput, setAdditionalFeedbackInput] = useState<string>(props.additionalFeedback ?? "");
  const [showAdditional, setShowAdditional] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const { id, createdAt, ...feedback } = props;
  const { conversationId } = useContext(ChatSessionContext);

  const FeedbackReasons = [
    { value: '', i18nKey: "feedback_select_reason" },
    { value: 'too_slow', i18nKey: "feedback_too_slow" },
    { value: 'not_accurate', i18nKey: "feedback_not_accurate" },
    { value: 'tone', i18nKey: "feedback_tone" },
    { value: 'other', i18nKey: "feedback_other" }
  ].map(
    option => ({
      value: option.value,
      label: L.ai_coach[option.i18nKey as keyof Omit<typeof L.ai_coach, 'phases' | 'dashboard'>]
    })
  );

  const selectedReason = useMemo(() => {
    return FeedbackReasons.find(r => r.value === props.reason) ?? FeedbackReasons[0];
  }, [props.reason, lang]);

  const sendFeedback = useCallback(async (feedback: NonNullable<AKMeta['feedback']>) => {
    if (createdAt && conversationId) {
      await updateFeedback({
        conversationId,
        createdAt,
        feedback
      });
    }
    dispatch({
      type: "update_meta",
      id,
      meta: { feedback }
    });
  }, [dispatch, updateFeedback, id, createdAt, conversationId]);

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2 text-sm">
        <button
          className={`${feedback.rating === "up" ? "text-indigo-600 opacity-100" : "text-gray-600 opacity-75"} hover:opacity-100 transition ease-in-out duration-100`}
          onClick={async () => {
            await sendFeedback({
              ...feedback,
              reason: null,
              additionalFeedback: null,
              rating: props.rating === "up" ? undefined : "up"
            });
            setShowSuccess(true);
          }}>
          <Icon size="md" name="HandThumbUpIcon" type="solid" />
        </button>

        <button
          className={`${feedback.rating === "down" ? "text-indigo-600 opacity-100" : "text-gray-600 opacity-75"} hover:opacity-100 transition ease-in-out duration-100`}
          onClick={async () => {
            const newRating = props.rating === "down" ? undefined : "down";
            await sendFeedback({
              ...feedback,
              rating: newRating
            });
            setShowAdditional(newRating === "down");
            setShowSuccess(!!(feedback.reason && feedback.reason !== "other"));
          }}
        >
          <Icon size="md" name="HandThumbDownIcon" type="solid" />
        </button>
        <div>{L.ai_coach.feedback_was_this_helpful}</div>
      </div>

      {(feedback.rating === "down" && showAdditional) && (
        <Alert variant="secondary" className="p-2">
          <Select
            label={L.ai_coach.feedback_tell_us_more}
            labelLocation="above"
            anchor={null} // <= this causes the dropdown options to be rendered NOT in a portal
            description={L.ai_coach.feedback_select_reason}
            options={selectedReason ? FeedbackReasons.slice(1) : FeedbackReasons}
            value={selectedReason}
            onChange={async (option) => {
              if (option.value) {
                await sendFeedback({
                  ...feedback,
                  reason: option.value
                });
                setShowSuccess(!!(option.value && option.value !== "other"));
              }
            }}
          />
          {props.reason === 'other' && <form
            className="flex flex-col gap-2"
            onSubmit={
              async e => {
                e.preventDefault();
                await sendFeedback({
                  ...feedback,
                  additionalFeedback: additionalFeedbackInput
                });
                setShowSuccess(true)
              }
            }>
            <TextArea
              rows={2}
              label={L.ai_coach.feedback_additional}
              value={additionalFeedbackInput}
              onChange={e => setAdditionalFeedbackInput(e.target.value)}
            />
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                className="self-start"
                size="sm"
                disabled={!additionalFeedbackInput}
                type="submit"
              >
                {L.applicant.meeting.submit}
              </Button>
            </div>
          </form>}
        </Alert>
      )}
      {(showSuccess && feedback.rating) && <Alert variant="success" className="p-2">{L.ai_coach.feedback_thanks}</Alert>}
    </div>
  );
};
